import React, {useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import {Radar} from 'react-chartjs-2';
import {Link, useSearchParams} from 'react-router-dom';
import Card from '@/components/ui/Card';
import PageHeader from '@/components/layout/PageHeader';
import LoadingStatus from '@/components/ui/LoadingStatus';
import {useTheme} from '@/context/ThemeContext';
import {useWorkspace} from '@/context/WorkspaceContext';
import {PlayerBrowseItem, PlayerComparisonV2Benchmark, PlayerComparisonV2Item, PlayerScope, playersV2API} from '@/features/players/api';
import {
  ComparisonUrlState,
  comparisonPageCodec,
  updateComparisonSelection,
} from '@/features/players/comparisonUrlState';
import {
  buildComparisonChartOptions,
  buildComparisonDatasets,
  comparisonSkills,
  comparisonSkillValues,
} from './PlayerComparison/chartConfig';
import WyscoutPerformanceProfile from './PlayerComparison/WyscoutPerformanceProfile';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

type SelectedPlayerSummary = Pick<PlayerBrowseItem, 'id' | 'name' | 'position' | 'age'> & {scope?: PlayerScope};
const emptyBrowsePlayers: PlayerBrowseItem[] = [];
const emptyComparedPlayers: PlayerComparisonV2Item[] = [];

const PlayerComparisonPage: React.FC = () => {
  const {currentWorkspace} = useWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';
  return <PlayerComparisonWorkspace key={workspaceId} workspaceId={workspaceId} />;
};

const PlayerComparisonWorkspace: React.FC<{workspaceId: string}> = ({workspaceId}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {theme} = useTheme();
  const [notice, setNotice] = useState('');
  const [knownPlayers, setKnownPlayers] = useState<Map<string, SelectedPlayerSummary>>(new Map());
  const state = comparisonPageCodec.parse(searchParams);
  const activeDiscovery = state[state.scope];
  const [search, setSearch] = useState(activeDiscovery.q);
  const rawSearch = searchParams.toString();
  const canonicalParams = comparisonPageCodec.serialize(state);
  const canonicalSearch = canonicalParams.toString();

  useEffect(() => {
    if (rawSearch !== canonicalSearch) setSearchParams(canonicalParams, {replace: true});
  }, [canonicalParams, canonicalSearch, rawSearch, setSearchParams]);

  useEffect(() => setSearch(activeDiscovery.q), [activeDiscovery.q, state.scope]);

  const browse = useQuery({
    queryKey: ['v2', 'players', workspaceId, 'comparison-options', state.scope, activeDiscovery],
    queryFn: () => playersV2API.browse(state.scope, {
      q: activeDiscovery.q, page: activeDiscovery.page, pageSize: 20, sort: 'name', order: 'asc', playedLastMatch: false,
      watching: state.scope === 'external' ? state.external.watching : false,
    }, workspaceId),
    enabled: Boolean(workspaceId),
  });
  const comparison = useQuery({
    queryKey: ['v2', 'players', workspaceId, 'comparison', state.players],
    queryFn: () => playersV2API.compare(state.players, workspaceId),
    enabled: Boolean(workspaceId) && state.players.length >= 2,
  });

  const players = browse.data?.data ?? emptyBrowsePlayers;
  const canCompare = browse.data?.capabilities.includes('player.compare') ?? true;
  const comparedPlayers = comparison.data?.data.players ?? emptyComparedPlayers;
  const benchmarks = comparison.data?.data.benchmarks ?? [];
  const selectedKey = state.players.join(',');

  useEffect(() => {
    setKnownPlayers(previous => {
      const next = new Map(previous);
      let changed = false;
      const selected = new Set(selectedKey ? selectedKey.split(',') : []);
      for (const id of Array.from(next.keys())) {
        if (!selected.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      for (const player of players) {
        if (!selected.has(player.id)) continue;
        const summary = {id: player.id, name: player.name, position: player.position, age: player.age, scope: player.scope};
        if (JSON.stringify(next.get(player.id)) !== JSON.stringify(summary)) {
          next.set(player.id, summary);
          changed = true;
        }
      }
      for (const player of comparedPlayers) {
        if (!selected.has(player.id)) continue;
        const existing = next.get(player.id);
        const summary = {id: player.id, name: player.name, position: player.position, age: player.age, scope: existing?.scope};
        if (JSON.stringify(existing) !== JSON.stringify(summary)) {
          next.set(player.id, summary);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, [comparedPlayers, players, selectedKey]);

  const commitState = (next: ComparisonUrlState, replace = false) => {
    setSearchParams(comparisonPageCodec.serialize(next), {replace});
  };

  const updateScopeState = (scope: PlayerScope, change: Partial<ComparisonUrlState[PlayerScope]>) => {
    commitState({...state, [scope]: {...state[scope], ...change}});
  };

  const togglePlayer = (playerID: string) => {
    const update = updateComparisonSelection(state.players, playerID);
    if (update.rejected) {
      setNotice('You can compare up to four players.');
      return;
    }
    setNotice('');
    commitState({...state, players: update.selection});
  };

  const scopeLabel = state.scope === 'squad' ? 'squad' : 'external';
  const total = browse.data?.meta.total ?? 0;
  const totalPages = browse.data?.meta.totalPages ?? 0;
  const page = activeDiscovery.page;
  const rangeStart = total === 0 ? 0 : (page - 1) * 20 + 1;
  const rangeEnd = Math.min(page * 20, total);

  useEffect(() => {
    if (!browse.data) return;
    const validPage = browse.data.meta.total === 0 ? 1 : Math.max(1, browse.data.meta.totalPages);
    if (activeDiscovery.page > validPage) {
      setSearchParams(comparisonPageCodec.serialize({...state, [state.scope]: {...activeDiscovery, page: validPage}}), {replace: true});
    }
  }, [activeDiscovery, browse.data, setSearchParams, state]);

  const radarData = {
    labels: [...comparisonSkills],
    datasets: buildComparisonDatasets(comparedPlayers, benchmarks, theme),
  };

  return (
    <div className="space-y-6 text-base text-gray-900 dark:text-gray-100">
      <PageHeader title="Player comparison" subtitle="Compare staff scouting profiles with Wyscout performance, training load and availability data" />

      <Card className="p-4 sm:p-6">
        <fieldset>
          <legend className="mb-2 font-semibold">Player source</legend>
          <div className="flex flex-wrap gap-4">
            {(['squad', 'external'] as const).map(scope => (
              <label key={scope} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-4 dark:border-gray-600">
                <input type="radio" name="comparison-scope" value={scope} checked={state.scope === scope} onChange={() => commitState({...state, scope})} />
                {scope === 'squad' ? 'Squad' : 'External'}
              </label>
            ))}
          </div>
        </fieldset>

        <form className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={event => {
          event.preventDefault();
          updateScopeState(state.scope, {q: search.trim(), page: 1});
        }}>
          <div className="w-full sm:max-w-md">
            <label htmlFor="comparison-search" className="mb-2 block font-medium">Find a player</label>
            <input
              id="comparison-search"
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              placeholder={`Search ${scopeLabel} players`}
            />
          </div>
          {state.scope === 'external' && (
            <label className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-gray-300 px-3 dark:border-gray-600">
              <input
                type="checkbox"
                checked={state.external.watching}
                onChange={event => commitState({
                  ...state,
                  external: {...state.external, watching: event.target.checked, page: 1},
                })}
              />
              Watching only
            </label>
          )}
          <button type="submit" className="min-h-11 rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600">Search players</button>
        </form>

        <section aria-label="Selected players" className="mt-5 rounded-xl bg-gray-50 p-4 dark:bg-gray-800/60">
          <h2 className="font-semibold">Selected players ({state.players.length} of 4)</h2>
          {state.players.length === 0 ? <p className="mt-2 text-gray-600 dark:text-gray-300">No players selected.</p> : (
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {state.players.map(id => {
                const player = knownPlayers.get(id);
                return <li key={id} className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900"><span className="min-w-0 break-words"><span className="block font-medium">{player?.name ?? id}</span>{player && <span className="block text-sm text-gray-600 dark:text-gray-300">{player.position}, age {player.age}</span>}</span><button type="button" onClick={() => togglePlayer(id)} aria-label={`Remove ${player?.name ?? id}`} className="min-h-11 shrink-0 rounded-lg border border-gray-300 px-3 dark:border-gray-600">Remove</button></li>;
              })}
            </ul>
          )}
        </section>

        {notice && <p role="status" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">{notice}</p>}
        {!canCompare && <p role="alert" className="mt-4 text-red-700 dark:text-red-300">You do not have permission to compare players.</p>}
        {browse.isLoading && <LoadingStatus label={`Loading ${scopeLabel} players...`} className="mt-4" />}
        {browse.isFetching && !browse.isLoading && <LoadingStatus label={`Updating ${scopeLabel} player results`} className="mt-4" />}
        {browse.isError && (
          <div role="alert" className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-100">
            <p>Unable to load {scopeLabel} players.</p>
            <button type="button" onClick={() => browse.refetch()} aria-label={`Retry loading ${scopeLabel} players`} className="mt-2 min-h-11 rounded-lg border border-red-700 px-3 py-2 font-semibold hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 dark:border-red-300 dark:hover:bg-red-900">Retry</button>
          </div>
        )}

        {!browse.isLoading && !browse.isError && <p aria-live="polite" className="mt-4 text-gray-600 dark:text-gray-300">{total} {scopeLabel} {total === 1 ? 'player' : 'players'}</p>}

        {!browse.isLoading && !browse.isError && total === 0 && <p className="mt-4">{activeDiscovery.q ? <>No {scopeLabel} players match &ldquo;{activeDiscovery.q}&rdquo;.</> : <>No {scopeLabel} players available.</>}</p>}

        <fieldset className="mt-5" disabled={!canCompare}>
          <legend className="sr-only">{scopeLabel} player results</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {players.map((player: PlayerBrowseItem) => {
              const checked = state.players.includes(player.id);
              return (
                <label key={player.id} className={`flex min-h-20 cursor-pointer items-center gap-3 rounded-xl border p-4 focus-within:ring-2 focus-within:ring-blue-600 ${checked ? 'border-blue-600 bg-blue-50 dark:border-blue-400 dark:bg-blue-950' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => togglePlayer(player.id)}
                    aria-label={`Select ${player.name}`}
                    className="h-5 w-5 shrink-0 rounded border-gray-400 text-blue-600 focus:ring-blue-600"
                  />
                  <span className="min-w-0">
                    <Link to={`${state.scope === 'squad' ? '/players' : '/scouting'}/${player.id}`} onClick={event => event.stopPropagation()} className="block truncate font-semibold text-blue-700 underline-offset-4 hover:underline dark:text-blue-300">{player.name}</Link>
                    <span className="block text-sm text-gray-600 dark:text-gray-300">{player.position}, age {player.age}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {!browse.isLoading && !browse.isError && total > 0 && (
          <nav aria-label="Player result pages" className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p>Showing {rangeStart}-{rangeEnd} of {total} players. Page {page} of {totalPages}.</p>
            <div className="flex gap-2">
              <button type="button" aria-label="Previous player results" disabled={page <= 1 || browse.isFetching} onClick={() => updateScopeState(state.scope, {page: page - 1})} className="min-h-11 rounded-lg border border-gray-300 px-4 disabled:opacity-50 dark:border-gray-600">Previous</button>
              <button type="button" aria-label="Next player results" disabled={page >= totalPages || browse.isFetching} onClick={() => updateScopeState(state.scope, {page: page + 1})} className="min-h-11 rounded-lg border border-gray-300 px-4 disabled:opacity-50 dark:border-gray-600">Next</button>
            </div>
          </nav>
        )}
      </Card>

      {state.players.length < 2 && (
        <Card className="p-6"><p className="text-gray-600 dark:text-gray-300">Select at least two players to load comparison metrics.</p></Card>
      )}
      {comparison.isLoading && <Card className="p-6"><LoadingStatus label="Loading comparison..." /></Card>}
      {comparison.isError && <Card className="p-6"><p role="alert" className="text-red-700 dark:text-red-300">Unable to load comparison data.</p></Card>}

      {comparedPlayers.length > 0 && (
        <Card className="p-4 sm:p-6">
          <h2 className="text-xl font-semibold">Staff scouting profile</h2>
          <div className="mx-auto mt-4 max-w-3xl" aria-label="Player skill radar chart">
            <Radar data={radarData} options={buildComparisonChartOptions(theme)} />
          </div>
        </Card>
      )}

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table aria-label="Player skill comparison data" className="min-w-full text-left text-base">
            <thead className="bg-gray-100 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Player</th>
                {comparisonSkills.map(skill => <th key={skill} scope="col" className="px-4 py-3 font-semibold">{skill}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {comparedPlayers.map((player: PlayerComparisonV2Item) => (
                <tr key={player.id}>
                  <th scope="row" className="whitespace-nowrap px-4 py-3"><Link to={`/players/${player.id}`} className="text-blue-700 hover:underline dark:text-blue-300">{player.name}</Link></th>
                  {comparisonSkillValues(player).map((value, index) => <td key={comparisonSkills[index]} className="px-4 py-3">{value}</td>)}
                </tr>
              ))}
              {benchmarks.map((benchmark: PlayerComparisonV2Benchmark) => (
                <tr key={`${benchmark.position}-${benchmark.scope}`} className="bg-gray-50 dark:bg-gray-800/60">
                  <th scope="row" className="whitespace-nowrap px-4 py-3">{benchmark.position} {benchmark.scope} benchmark</th>
                  {comparisonSkillValues(benchmark).map((value, index) => <td key={comparisonSkills[index]} className="px-4 py-3">{value}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {comparedPlayers.length > 0 && (
        <>
          <ComparisonMetrics players={comparedPlayers} />
          <BenchmarkMetrics benchmarks={benchmarks} />
          <WyscoutPerformanceProfile players={comparedPlayers} benchmarks={benchmarks} />
        </>
      )}
    </div>
  );
};

const ComparisonMetrics: React.FC<{players: PlayerComparisonV2Item[]}> = ({players}) => (
  <Card className="p-0">
    <div className="overflow-x-auto">
      <table aria-label="Player performance comparison data" className="min-w-full text-left text-base">
        <thead className="bg-gray-100 dark:bg-gray-800"><tr><th className="px-4 py-3">Player</th><th className="px-4 py-3">Rating (30d)</th><th className="px-4 py-3">Goals</th><th className="px-4 py-3">Assists</th><th className="px-4 py-3">Attendance</th><th className="px-4 py-3">RPE</th><th className="px-4 py-3">ACWR</th><th className="px-4 py-3">Risk</th></tr></thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {players.map(player => <tr key={player.id}><th scope="row" className="px-4 py-3">{player.name}</th><td className="px-4 py-3">{player.performance.average_rating_30d}</td><td className="px-4 py-3">{player.performance.goals}</td><td className="px-4 py-3">{player.performance.assists}</td><td className="px-4 py-3">{player.training.attendance_rate_30d}%</td><td className="px-4 py-3">{player.training.average_rpe_30d}</td><td className="px-4 py-3">{player.load.acwr}</td><td className="px-4 py-3">{player.load.risk}</td></tr>)}
        </tbody>
      </table>
    </div>
  </Card>
);

const BenchmarkMetrics: React.FC<{benchmarks: PlayerComparisonV2Benchmark[]}> = ({benchmarks}) => (
  <Card className="p-0">
    <div className="overflow-x-auto">
      <table aria-label="Position benchmark comparison data" className="min-w-full text-left text-base">
        <thead className="bg-gray-100 dark:bg-gray-800"><tr><th className="px-4 py-3">Position</th><th className="px-4 py-3">Sample size</th><th className="px-4 py-3">Rating (30d)</th><th className="px-4 py-3">Goals</th><th className="px-4 py-3">Assists</th><th className="px-4 py-3">Attendance</th><th className="px-4 py-3">RPE</th><th className="px-4 py-3">ACWR</th></tr></thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {benchmarks.map(benchmark => <tr key={`${benchmark.position}-${benchmark.scope}`}><th scope="row" className="px-4 py-3">{benchmark.position} {benchmark.scope} benchmark</th><td className="px-4 py-3">{benchmark.sample_size}</td><td className="px-4 py-3">{benchmark.performance.average_rating_30d}</td><td className="px-4 py-3">{benchmark.performance.goals}</td><td className="px-4 py-3">{benchmark.performance.assists}</td><td className="px-4 py-3">{benchmark.training.attendance_rate_30d}%</td><td className="px-4 py-3">{benchmark.training.average_rpe_30d}</td><td className="px-4 py-3">{benchmark.load.acwr}</td></tr>)}
        </tbody>
      </table>
    </div>
  </Card>
);

export default PlayerComparisonPage;
